import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Flag, Loader2, RefreshCw } from 'lucide-react';
import type {
  ConsoleCurrentParticipant,
  ConsoleData,
  ConsoleParticipant,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { FlagsSidebar } from '@/components/scenarios/FlagsSidebar';
import type { IActiveScenarioDetailParticipant } from '@/types/scenarios';
import { useGetExerciseActiveConsoleQuery } from '@/store/apiSlice';
import { useExerciseActiveRunWebSocket } from '@/hooks/useExerciseActiveRunWebSocket';

function getRtkErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (data && typeof data === 'object' && data !== null && 'detail' in data) {
      const d = (data as { detail?: unknown }).detail;
      if (typeof d === 'string') return d;
    }
  }
  if (error && typeof error === 'object' && 'error' in error) {
    const e = (error as { error?: unknown }).error;
    if (typeof e === 'string') return e;
  }
  if (error instanceof Error) return error.message;
  return 'Failed to load console. Please try again.';
}

type DerivedConsole = {
  vncUrl: string | null;
  participants: ConsoleParticipant[];
  currentParticipant: IActiveScenarioDetailParticipant | null;
  error: string | null;
};

function deriveConsoleState(
  data: ConsoleData | undefined,
  searchParams: URLSearchParams,
): DerivedConsole {
  const token = searchParams.get('token');
  const path = searchParams.get('path');
  const emptyP: ConsoleParticipant[] = [];

  if (!data) {
    if (token || path) {
      const vncBaseUrl = 'http://124.123.17.12:6081/vnc_lite.html';
      const queryParams = new URLSearchParams();

      if (path) {
        queryParams.set('path', path);
      } else if (token) {
        const encodedPath = encodeURIComponent(`?token=${token}`);
        queryParams.set('path', encodedPath);
      }

      return {
        vncUrl: `${vncBaseUrl}?${queryParams.toString()}`,
        participants: emptyP,
        currentParticipant: null,
        error: null,
      };
    }

    return {
      vncUrl: null,
      participants: emptyP,
      currentParticipant: null,
      error: 'Failed to load console data. Please check the scenario configuration.',
    };
  }

  const directUrl = data.console_url ?? data.vnc_url;
  if (directUrl) {
    const cp = data.current_participant as ConsoleCurrentParticipant | undefined;
    const currentParticipant =
      cp && data.scenario?.type === 'FLAG'
        ? (cp as IActiveScenarioDetailParticipant)
        : null;
    return {
      vncUrl: directUrl,
      participants: data.participants?.length ? data.participants : emptyP,
      currentParticipant,
      error: null,
    };
  }

  if (data.active_scenario_id && !data.vnc_host) {
    const cp = data.current_participant as ConsoleCurrentParticipant | undefined;
    const currentParticipant =
      cp && data.scenario?.type === 'FLAG'
        ? (cp as IActiveScenarioDetailParticipant)
        : null;
    return {
      vncUrl: null,
      participants: data.participants?.length ? data.participants : emptyP,
      currentParticipant,
      error: null,
    };
  }

  if (data.vnc_host && (data.token || data.path)) {
    const protocol =
      data.vnc_port === 443 || data.vnc_port === 8443 ? 'https' : 'http';
    const port = data.vnc_port ? `:${data.vnc_port}` : '';
    const vncBaseUrl = `${protocol}://${data.vnc_host}${port}/vnc_lite.html`;
    const queryParams = new URLSearchParams();

    if (data.path) {
      queryParams.set('path', data.path);
    } else if (data.token) {
      const encodedPath = encodeURIComponent(`?token=${data.token}`);
      queryParams.set('path', encodedPath);
    }

    return {
      vncUrl: `${vncBaseUrl}?${queryParams.toString()}`,
      participants: data.participants ?? emptyP,
      currentParticipant: null,
      error: null,
    };
  }

  if (data.token && !data.vnc_host) {
    return {
      vncUrl: null,
      participants: emptyP,
      currentParticipant: null,
      error: 'VNC server host not found. Please check the scenario configuration.',
    };
  }

  return {
    vncUrl: null,
    participants: emptyP,
    currentParticipant: null,
    error: 'Incomplete console data. Please check the scenario configuration.',
  };
}

export default function ConsolePage() {
  const { id } = useParams<{ id: string }>();
  useExerciseActiveRunWebSocket(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const participantId = searchParams.get('participant') || searchParams.get('player');
  const vncUrlParam = searchParams.get('vnc_url');

  const directVncUrl = useMemo(() => {
    if (!vncUrlParam) return null;
    try {
      return decodeURIComponent(vncUrlParam);
    } catch {
      return vncUrlParam;
    }
  }, [vncUrlParam]);

  const skipConsoleQuery = !id || Boolean(vncUrlParam);

  const {
    data: consoleData,
    isLoading: consoleLoading,
    isError: consoleIsError,
    error: consoleError,
    refetch,
  } = useGetExerciseActiveConsoleQuery(
    { activeId: id ?? '', participant: participantId },
    { skip: skipConsoleQuery },
  );

  const [showFlagsSidebar, setShowFlagsSidebar] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);

  const derived = useMemo((): DerivedConsole => {
    if (directVncUrl) {
      return {
        vncUrl: directVncUrl,
        participants: [],
        currentParticipant: null,
        error: null,
      };
    }
    if (consoleIsError) {
      return {
        vncUrl: null,
        participants: [],
        currentParticipant: null,
        error: getRtkErrorMessage(consoleError),
      };
    }
    return deriveConsoleState(consoleData, searchParams);
  }, [directVncUrl, consoleData, searchParams, consoleIsError, consoleError]);

  const { vncUrl, participants, currentParticipant, error: derivedError } = derived;
  const blockingError = iframeError ?? derivedError;

  const showLoading = !directVncUrl && !skipConsoleQuery && consoleLoading;

  if (!id) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background p-4">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-medium text-foreground">Scenario ID is required</p>
        </div>
      </div>
    );
  }

  if (showLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading console...</p>
        </div>
      </div>
    );
  }

  if (blockingError) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background p-4">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-medium text-foreground">{blockingError}</p>
          <p className="text-xs text-muted-foreground">
            If this problem persists, please contact support.
          </p>
        </div>
      </div>
    );
  }

  if (!vncUrl) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-6">
          <AlertCircle className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No console URL available</p>
        </div>
      </div>
    );
  }

  const handleParticipantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    if (newId && id) {
      setSearchParams({ participant: newId });
    }
  };

  return (
    <div className="flex h-screen w-full flex-col bg-background">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-border bg-card px-4 py-2.5 shadow-sm">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild className="text-foreground hover:bg-muted">
            <Link to={`/scenarios/active/${id}`} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to scenario
            </Link>
          </Button>
          <span className="text-muted-foreground" aria-hidden>
            |
          </span>
          <span className="text-sm font-medium text-foreground">Console</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIframeError(null);
              if (!directVncUrl && !skipConsoleQuery) void refetch();
            }}
            className="gap-2 text-muted-foreground hover:text-foreground"
            title="Reload console"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
        <div className="flex items-center gap-3">
          {participants.length > 1 && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="console-participant"
                className="text-sm font-medium text-muted-foreground"
              >
                Participant
              </label>
              <Select
                id="console-participant"
                value={participantId || (participants[0]?.id ?? '')}
                onChange={handleParticipantChange}
                className="w-[200px] border-border bg-background text-foreground"
              >
                {participants.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name || p.id} {p.role ? `(${p.role})` : ''}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {currentParticipant && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFlagsSidebar(true)}
              className="gap-2"
            >
              <Flag className="h-4 w-4" />
              View Flags{' '}
              {(currentParticipant.flag_data?.length ?? 0) > 0
                ? `(${currentParticipant.flag_data?.length})`
                : ''}
            </Button>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 bg-muted/30">
        <iframe
          src={vncUrl}
          className="h-full w-full border-0 bg-background"
          title="VNC Console"
          allow="clipboard-read; clipboard-write"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
          style={{
            display: 'block',
          }}
          onError={() => {
            setIframeError(
              'Failed to load the console. This may be due to mixed content restrictions (HTTPS page loading HTTP iframe). Please ensure the VNC server supports HTTPS or use a reverse proxy.',
            );
          }}
          onLoad={() => {
            setIframeError(null);
          }}
        />
      </div>
      <FlagsSidebar
        participant={showFlagsSidebar ? currentParticipant : null}
        isOpen={showFlagsSidebar}
        onClose={() => setShowFlagsSidebar(false)}
      />
      <noscript>
        <div className="flex h-screen items-center justify-center bg-background">
          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-lg font-semibold text-foreground">Console Unavailable</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              JavaScript is required to load the console.
            </p>
          </div>
        </div>
      </noscript>
    </div>
  );
}
